import { Inject, Injectable } from '@angular/core';
import { APP_STORE_CONFIG_TOKEN, MODELS_STORE_NAME_TOKEN } from '@ddt-web/config';
import { ApiStoreConfiguration, AppStoreConfig } from '@ddt-web/data-service';
import { WweBenchmarkPosition } from '@ddt-web/domain';
import { StoreService } from '@ddt-web/store-service';
import { PaQueryParamKey, UrlHistoryService } from '@ddt-web/url';
import * as castArray from 'lodash/castArray';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import { combineLatest } from 'rxjs';
import { first } from 'rxjs/operators';

import { AbstractWorker } from '../models/abstract-worker';
import { PasDataLoaderService } from '../pas-setup-data-workers/services/pas-data-loader.service';
import { PortfolioResolverUtil } from '../utils';

@Injectable()
export class BenchmarkResolverWorker extends AbstractWorker {
	public shouldLoadData: boolean = true;
	public ids: string[];
	public benchmarkId: string;
	public internalPortfolios;
	public externalPortfolios;
	public partnerPortfolios;
	public previousRoute: string;

	constructor(
		public storeService: StoreService,
		protected pasDataLoader: PasDataLoaderService,
		protected urlHistoryService: UrlHistoryService,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig,
		@Inject(MODELS_STORE_NAME_TOKEN) public modelsStoreName: string
	) {
		super();
	}

	/**
	 * 1) [Set/change benchmark] only needs to make risk & performance(performanceCommonPeriod) calls
	 * 2) [Unselect benchmark] will not make any api calls
	 * 3) [Deep link with acBenchmark query param/refresh page/edit portfolio/add a new portfolio to compare(set as benchmark or not)] will make all the api calls as normal
	 */
	public handle(queryParams: any): void {
		this.shouldLoadData = true;
		this.ids = castArray(get(queryParams, 'portfolios', []));
		this.benchmarkId = get(queryParams, PaQueryParamKey.AC_BENCHMARK);
		const history = this.urlHistoryService.history;
		this.previousRoute = history[history.length - 2] || '';
		this.internalPortfolios = PortfolioResolverUtil.extractInternalPortfolioIds(this.ids);
		this.externalPortfolios = PortfolioResolverUtil.extractExternalPortfolioIds(this.ids);
		this.partnerPortfolios = PortfolioResolverUtil.extractPartnerPortfolioIds(this.ids);


		combineLatest([
			this.storeService.selectAll(this.storeConfig[this.modelsStoreName]),
			this.storeService.selectByIds(this.storeConfig.associatedPortfolios, this.ids),
			this.storeService.selectByIds(this.storeConfig.details, this.ids)
		]).pipe(
			first(([config, associatedPortfolios, details]: any) => {
				const isExternalPortfoliosSuccessfullyLoaded = this.isPortfoliosSuccessfullyLoaded(this.externalPortfolios, associatedPortfolios);
				const isPartnerPortfoliosSuccessfullyLoaded = this.isPortfoliosSuccessfullyLoaded(this.partnerPortfolios, associatedPortfolios);
				const isInternalPortfoliosSuccessfullyLoaded = this.isPortfoliosSuccessfullyLoaded(this.internalPortfolios, associatedPortfolios);

				return config
					&& !config.isLoading
					&& !associatedPortfolios.isLoading
					&& isExternalPortfoliosSuccessfullyLoaded
					&& isInternalPortfoliosSuccessfullyLoaded
					&& isPartnerPortfoliosSuccessfullyLoaded;
			})
		).subscribe(([config, associatedPortfolios, details]) => {
			if (this.benchmarkId) {
				this.requestPortfolioDetails(this.ids, config, associatedPortfolios, details, this.benchmarkId, this.previousRoute);
				this.shouldLoadData = false;
			} else {
				if (this.urlHistoryService.isQueryParamUpdated(PaQueryParamKey.AC_BENCHMARK)) {
					this.storeService.removeAll(this.storeConfig.cohortBenchmarkDetail, null);
				}
			}
		});
	}

	protected requestPortfolioDetails(ids: string[], config: any, associatedPortfolios: any, details: any, benchmarkId: string, previousRoute: string): void {
		if (this.shouldLoadData) {
			const additionalAnalysisParams = {
				currency: 'USD', identifierToBeUsedAsWweApiParamId: 'identifier', benchmark: { portfolioBenchmarkId: benchmarkId } as WweBenchmarkPosition
			};

			if (ids.every(id => !!details.entities[id]) && !previousRoute.includes('portfolio-editor')) {
				// When a benchmark is changed:
				const configArray: ApiStoreConfiguration[] = [this.storeConfig.risk, this.storeConfig.performance];
				this.pasDataLoader.populateStoresForPortfolios(ids, config.entities, associatedPortfolios, additionalAnalysisParams, benchmarkId, [configArray[0]], true, true);

			} else {
				// On initial load:
				this.pasDataLoader.populateStoresForPortfolios(ids, config.entities, associatedPortfolios, additionalAnalysisParams, benchmarkId);
			}
		}

		// temp use new store until details holdings is migrated to asset resolve
		this.storeService.selectAll(this.storeConfig.details).pipe(
			first((details: any) => {
				return !details?.isLoading && ids.every(id => !!details?.entities?.[id]);
			})
		).subscribe((details) => {
			this.updateCohortStores(details, benchmarkId);
		});
	}

	private updateCohortStores(details: any, benchmarkId: string): void {
		this.storeService.upsertMany(this.storeConfig.cohortBenchmarkDetail, details.ids.map(id => {
			// cohortBenchmarkDetail store is special, for every entity in this store, the id is portfolio id, but the entity data is benchmark data, this way our data config was able to extract portfolio & benchmark data at same time for each portfolio.
			if (id === benchmarkId) {
				return Object.assign({}, details.entities[benchmarkId], { id: id, isBenchmark: true });
			} else {
				return Object.assign({}, details.entities[benchmarkId], { id: id, isBenchmark: false });
			}
		}));
	}

	protected isPortfoliosSuccessfullyLoaded(portfolioIds: any[], associatedPortfolios: any): boolean {
		// TODO Waris: check portfolio resolver util to align the logic if needed
		const portfoliosMissingData = portfolioIds.filter(id => !get(associatedPortfolios, `entities.${id}`));
		const hasFailedPortfolio = portfolioIds.some(id => get(associatedPortfolios.entities[id], 'errorCode') || get(associatedPortfolios.entities[id], 'error'));

		return isEmpty(portfoliosMissingData) && !hasFailedPortfolio;
	}
}
